import { heicTo } from 'heic-to';

import { getFileMimeType } from './truncateString';

export const CASE_ATTACHMENT_ACCEPT =
  '.pdf, .doc, .docx, .ppt, .pptx, .png, .jpg, .jpeg, .jfif, .pjpeg, .pjp, .heic, .heif, .stl';

const CASE_ATTACHMENT_EXTENSIONS = new Set([
  'pdf',
  'doc',
  'docx',
  'ppt',
  'pptx',
  'png',
  'jpg',
  'jpeg',
  'jfif',
  'pjpeg',
  'pjp',
  'heic',
  'heif',
  'stl',
]);

const CASE_ATTACHMENT_MIME_TYPES = new Set([
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/sla',
  'application/vnd.ms-pki.stl',
  'model/stl',
]);

const getFileExtension = (filename: string): string => filename.split('.').pop()?.toLowerCase() || '';

export const isAcceptedCaseAttachmentFile = (file: File): boolean => {
  const mimeType = file.type.toLowerCase();
  if (mimeType.startsWith('image/')) return true;
  if (CASE_ATTACHMENT_MIME_TYPES.has(mimeType)) return true;

  return CASE_ATTACHMENT_EXTENSIONS.has(getFileExtension(file.name));
};

export const getUploadFileMimeType = (file: File): string => {
  const mimeTypeFromName = getFileMimeType(file.name);
  if (mimeTypeFromName === 'model/stl') return mimeTypeFromName;
  if (file.type && file.type !== 'application/octet-stream') return file.type;

  return mimeTypeFromName || file.type || 'application/octet-stream';
};

const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif']);
const HEIC_EXTENSIONS = new Set(['heic', 'heif']);

/**
 * Some browsers hand over iPhone photos with an empty `file.type`, so
 * the extension counts too.
 */
const isHeicFile = (file: File): boolean =>
  HEIC_MIME_TYPES.has(file.type.toLowerCase()) || HEIC_EXTENSIONS.has(getFileExtension(file.name));

/**
 * Only Safari can render HEIC. Uploaded as-is, an iPhone photo saves
 * fine but shows as a broken image in every other browser, so convert
 * it to JPEG first, as the clinical upload paths do.
 */
export const convertHeicToJpeg = async (file: File): Promise<File> => {
  if (!isHeicFile(file)) return file;

  const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.85 });
  return new File([jpegBlob], file.name.replace(/\.(heic|heif)$/i, '.jpeg'), { type: 'image/jpeg' });
};
